// reuse: extraction candidate for @g-a-l-a-c-t-i-c/platform (device-code CLI login client; see TODO.md).
import { classifyTokenResponse, parseDeviceCode } from './device-protocol.js';

export type DeviceLoginDeps = {
  post: (url: string, body: unknown) => Promise<{ status: number; body: unknown }>;
  sleep: (ms: number) => Promise<void>;
  now: () => number;
  openBrowser: (url: string) => Promise<void>;
  log: (msg: string) => void;
};

const MAX_CONSECUTIVE_POST_FAILURES = 3;

/**
 * Device-code login against SaaS Platform (`/api/device/code`, `/api/device/token`).
 * Pure: all I/O is injected. Never logs the device code or the returned key.
 */
export async function deviceLogin(
  baseUrl: string,
  deps: DeviceLoginDeps
): Promise<{ key: string; keyPrefix?: string }> {
  const start = await deps.post(`${baseUrl}/api/device/code`, {});
  const info = parseDeviceCode(start.status, start.body);

  deps.log(`Your code: ${info.userCode}\nApprove sign-in at: ${info.url}`);
  try {
    await deps.openBrowser(info.url);
  } catch (err) {
    // Intentional, documented swallow: a missing browser (headless/SSH) must not abort login;
    // the user approves from any device using the URL and code logged above.
    const reason = err instanceof Error ? err.message : String(err);
    deps.log(`Could not open a browser (${reason}); open ${info.url} manually`);
  }

  const deadline = deps.now() + info.expiresInSec * 1000;
  let interval = info.intervalSec;
  let failures = 0;

  for (;;) {
    if (deps.now() >= deadline) {
      throw new Error('Login code expired');
    }
    await deps.sleep(interval * 1000);

    let response: { status: number; body: unknown };
    try {
      response = await deps.post(`${baseUrl}/api/device/token`, { device_code: info.deviceCode });
    } catch (err) {
      failures += 1;
      if (failures >= MAX_CONSECUTIVE_POST_FAILURES) throw err;
      continue;
    }
    failures = 0;

    const result = classifyTokenResponse(response.status, response.body, interval);
    if (result.kind === 'done') {
      return result.keyPrefix === undefined
        ? { key: result.key }
        : { key: result.key, keyPrefix: result.keyPrefix };
    }
    interval = result.intervalSec;
  }
}
